import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  applySlip,
  CalAm,
  CalAmBulk,
  getInitFormData,
  getTablebySql,
  setUmByFgUm,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { showResizablePopupModal } from "@widgets/CommonModal";
import SN_SCT_POR_REG_001_POP_001 from "@menu/SCT/SN_SCT_POR_REG_001/SN_SCT_POR_REG_001_POP_01";
import SnButton from "@shared/components/SnButton";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnTab from "@shared/components/SnTab";
import { global, useMenuStore } from "@shared/libs/Global";
import { dateToString } from "@shared/utils/DateUtils";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import { RowNode } from "ag-grid-community";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { executeApprovalSubmit } from "@shared/utils/approval/approvalIntegration";
import { submitApproval } from "@shared/api/approvalSubmitApi";

interface Props {
  Menuprops: menuInfo;
}

// 외주발주등록
const SN_SCT_POR_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo, config } = global();
  // 외주발주 전자결재(아마란스) 연동: sys_config MAS0125='AM10' 일 때만 결재상신 노출
  const isApprovalVisible = config?.MAS0125?.dc_ctrl === "AM10";

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedTab, setSelectedTab] = useState("1");
  const [focusedIndex, setFocusedIndex] = useState();

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    const modifiedRowsD = gridRefs.current["grid3"].current.getChange();

    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0 ||
      modifiedRowsD.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_sct_por_reg_001_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();

      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const sctPohChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (sctPohChanges.length > 0) return false;

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const newRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        cd_dept: userInfo?.cd_dept,
        no_po: "",
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        cd_gp: userInfo?.cd_gp,
        dt_po: today,
        yn_app: "N",
        fg_trns: "1",
        fg_sct: "1",
        cd_exch: "000",
        fg_vat: "21",
        rt_exch: 1,
        cd_cust: searchInfo.cd_cust,
        nm_cust: searchInfo.nm_cust,
        am: 0,
        am_k: 0,
        am_vat: 0,
      };
      gridRefs.current["grid1"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onAddRow = () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null || focusedNodeH.rowIndex == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      if (selectedTab === "1") {
        return gridRefs.current["grid2"].current?.addRow({});
      } else if (selectedTab === "2") {
        const focusedNodeL = gridRefs.current["grid2"].current.focusedRowNode();
        if (focusedNodeL == null || focusedNodeL.rowIndex == null) {
          showDialog("alter", "선택된 발주내역이 없습니다.");
          return false;
        }
        return gridRefs.current["grid3"].current?.addRow({});
      }
    } catch (error: any) {}
  };

  const onBeforeAddRow2 = () => {
    try {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        no_line: maxNoLine + 1,
        dt_dlv: today,
        no_po: focusedNodeH.data?.no_po,
        st_po: focusedNodeH.data.yn_app === "Y" ? "R" : "F",
        cd_cust_sct: focusedNodeH.data?.cd_cust,
        cd_exch: focusedNodeH.data?.cd_exch,
        fg_sct: focusedNodeH.data?.fg_sct,
        fg_trns: focusedNodeH.data?.fg_trns,
        fg_vat: focusedNodeH.data?.fg_vat,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onBeforeAddRow3 = () => {
    try {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      const focusedNodeL = gridRefs.current["grid2"].current.focusedRowNode();

      let maxNoLine: number = 0;
      gridRefs.current["grid3"].current.forEachFilteredNode((node: RowNode) => {
        if (node.data.no_line_pol === focusedNodeL.data.no_line) {
          const value = node.data.no_line;
          if (value != null) {
            if (maxNoLine === null || value > maxNoLine) {
              maxNoLine = value;
            }
          }
        }
      });

      return {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        no_po: focusedNodeH.data?.no_po,
        no_line_pol: focusedNodeL.data?.no_line,
        no_line: maxNoLine + 1,
        cd_item: focusedNodeL.data?.cd_item,
        nm_item: focusedNodeL.data?.nm_item,
        fg_sct: focusedNodeH.data?.fg_sct,
        qt: focusedNodeL.data?.qt,
        fg_matl: "1",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const focusedNodeH = gridRefs.current["grid1"].current?.focusedRowNode();
    if (!focusedNodeH) return;

    let deleteFlag = false;
    gridRefs.current["grid2"].current?.forEachFilteredNode((node: RowNode) => {
      if (node.data.state === "insert") return;
      if (focusedNodeH.data.yn_app !== "Y") {
        if (node.data.st_po !== "P" && node.data.st_po !== "F") {
          deleteFlag = true;
        }
      } else {
        // 자동승인
        if (node.data.st_po !== "R") {
          deleteFlag = true;
        }
      }
    });
    if (deleteFlag) {
      await showDialog("alter", "진행중인 건입니다.");
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid3"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    const focusedNodeH = gridRefs.current["grid1"].current?.focusedRowNode();
    if (!focusedNodeH) return;

    const selectedRows = gridRefs.current["grid2"].current?.getSelectedRows();
    if (selectedRows.length < 1) return;

    const selectedRow = selectedRows[0];

    // const rowCount = gridRefs.current["grid2"].current?.getDisplayedRowCount();
    // if (rowCount === 1 && selectedRow.state !== "insert") {
    //   await showDialog("alter", "마지막 행입니다.");
    //   return;
    // }

    if (selectedRow.state !== "insert") {
      if (focusedNodeH.data.yn_app !== "Y") {
        if (selectedRow.st_po !== "P" && selectedRow.st_po !== "F") {
          await showDialog("alter", "진행중인 건입니다.");
          return;
        }
      } else {
        // 자동승인
        if (selectedRow.st_po !== "R") {
          await showDialog("alter", "진행중인 건입니다.");
          return;
        }
      }
    }

    if (selectedTab === "1") {
      if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;
      const focusedNodeL = gridRefs.current["grid2"].current?.focusedRowNode();

      const deletePodNodes: any[] = [];
      gridRefs.current["grid3"].current.forEachFilteredNode((node: RowNode) => {
        if (
          node.data.no_po === focusedNodeL.data.no_po &&
          node.data.no_line_pol === focusedNodeL.data.no_line
        ) {
          deletePodNodes.push(node);
        }
      });

      let qtReqFlag = false;
      for (const node of deletePodNodes) {
        if (node.data.qt_isureq !== 0) {
          qtReqFlag = true;
          return;
        }
      }
      if (qtReqFlag) {
        await showDialog(
          "alter",
          "출고의뢰량이 존재하는 건은 삭제할 수 없습니다."
        );
        return;
      }

      for (const node of deletePodNodes) {
        gridRefs.current["grid3"].current.deleteRowByIndex(node.rowIndex);
      }
      gridRefs.current["grid2"].current?.deleteSelectedRows();
    } else if (selectedTab === "2") {
      if (gridRefs.current["grid3"].current?.getDisplayedRowCount() < 1) return;
      const selectedRowsD =
        gridRefs.current["grid3"].current?.getSelectedRows();
      let qtReqFlag = false;
      for (const r of selectedRowsD) {
        if (r.qt_isureq !== 0) {
          qtReqFlag = true;
          return;
        }
      }
      if (qtReqFlag) {
        await showDialog(
          "alter",
          "출고의뢰량이 존재하는 건은 삭제할 수 없습니다."
        );
        return;
      }
      gridRefs.current["grid3"].current?.deleteSelectedRows();
    }
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();
      gridRefs.current["grid3"].current.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const requiredValuesCheckD =
        gridRefs.current["grid3"].current.requiredValuesCheck();
      if (!requiredValuesCheckD) return false;

      const sctPohChanges = gridRefs.current["grid1"].current.getChange();
      const sctPolChanges = gridRefs.current["grid2"].current.getChange();
      const sctPodChanges = gridRefs.current["grid3"].current.getChange();

      const headerInserts = sctPohChanges.insert ?? [];
      const lineInserts = sctPolChanges.insert ?? [];
      const detailInserts = sctPodChanges.insert ?? [];

      if (
        headerInserts.length > 0 &&
        lineInserts.length === 0 &&
        detailInserts.length === 0
      ) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        sctPohChanges.modifiedRowcount === 0 &&
        sctPolChanges.modifiedRowcount === 0 &&
        sctPodChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const bomCheckList = Array.from(
        new Map(
          lineInserts.map((l: any) => [
            `${l.cd_item}_${l.cd_cust_sct || l.cd_cust}_${l.cd_exch}`,
            {
              cd_item: l.cd_item,
              cd_cust: l.cd_cust_sct || l.cd_cust,
              cd_exch: l.cd_exch,
            },
          ])
        ).values()
      );
      if (bomCheckList.length > 0) {
        const result = await callApi("SN_SCT_POR_REG_001_SAVE_01_checkBom", {
          cd_firm: userInfo.cd_firm,
          cd_biz: userInfo.cd_biz,
          bomCheckList,
        });
        const data = result.data;
        if (data.length > 0) {
          const msg =
            "외주BOM이 등록되지 않은 품목이 있습니다.\n" +
            "품목 : " +
            data.map((i: any) => i.cd_item).join(", ") +
            "\n저장하시겠습니까?";
          const confirm = await showDialog("confirm", msg);
          if (!confirm) return;
        }
      }

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      if (headerInserts.length > 0) {
        const noSlip = await applySlip({
          cd_column: "no_po",
          cd_module: Menuprops.cd_module,
          no_line: 2,
          cd_prefix: "SCT",
          dt: focusedRowNodeH.data.dt_po,
          girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
          inserts: [sctPohChanges, sctPolChanges],
          cd_menu: Menuprops.cd_menu,
        });
        gridRefs.current["grid2"].current.applyFilters({ no_po: noSlip });
        gridRefs.current["grid3"].current?.forEachNode((node: any) => {
          if (node.data.no_po == "" || node.data.no_po == null) {
            node.setDataValue("no_po", noSlip);
          }
        });
        sctPodChanges.insert.forEach((row: any) => {
          if (row.no_po == "" || row.no_po == null) {
            row.no_po = noSlip;
          }
        });
        gridRefs.current["grid3"].current.applyFilters({ no_po: noSlip });
      }

      const saveData = {
        sct_poh: sctPohChanges,
        sct_pol: sctPolChanges,
        sct_pod: sctPodChanges,
      };

      const ok = await fnSave("SN_SCT_POR_REG_001_SAVE_01", saveData);
      if (!ok) {
        gridRefs.current["grid2"].current.resetUpdate(true);
        gridRefs.current["grid1"].current.resetUpdate(true);
        gridRefs.current["grid3"].current.resetUpdate(true);
        return false;
      }

      setFocusedIndex(focusedRowNodeH.rowIndex);
      gridRefs.current["grid1"].current?.resetUpdate();
      gridRefs.current["grid2"].current?.resetUpdate();
      gridRefs.current["grid3"].current?.resetUpdate();
      await onSearch();
      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid3"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id == "btnApply") {
      const sctPohChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (sctPohChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const component = (
        <SN_SCT_POR_REG_001_POP_001
          selectedData={{ cd_biz: selectedFormData.cd_biz }}
        />
      );
      const result = await showResizablePopupModal({
        title: "외주요청적용",
        children: component,
      });
      if (!result || result === "close") return;

      const headerResult = result[0];
      const lineResult = result[1];

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedRowNodeH && headerResult[0]) {
        focusedRowNodeH.setDataValue(
          "cd_biz",
          headerResult[0].cd_biz,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "cd_exch",
          headerResult[0].cd_exch,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "rt_exch",
          headerResult[0].rt_exch,
          "noEvent"
        );
      }

      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            addedNode.setDataValue("cd_item", result.cd_item, "noEvent");
            addedNode.setDataValue("nm_item", result.nm_item, "noEvent");
            addedNode.setDataValue(
              "dc_item_spec",
              result.dc_item_spec,
              "noEvent"
            );
            addedNode.setDataValue(
              "dc_item_unit",
              result.dc_item_unit,
              "noEvent"
            );
            addedNode.setDataValue("qt", result.qt_app, "noEvent");
            addedNode.setDataValue("um", result.um, "noEvent");
            addedNode.setDataValue("am", result.am, "noEvent");
            addedNode.setDataValue("um_k", result.um_k, "noEvent");
            addedNode.setDataValue("am_k", result.am_k, "noEvent");
            addedNode.setDataValue("am_vat", result.am_vat, "noEvent");
            addedNode.setDataValue("no_prq", result.no_prq, "noEvent");
            addedNode.setDataValue("no_line_prq", result.no_line, "noEvent");
            addedNode.setDataValue("dc_rmk", result.dc_rmk, "noEvent");
            addedNode.setDataValue("cd_pjt", result.cd_pjt, "noEvent");
            addedNode.setDataValue("nm_pjt", result.nm_pjt, "noEvent");
            addedNode.isPopup = true;
          }
        }
      });
      await CalAmBulk(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "rt_exch"
      );
    }

    if (id == "aButton_Approve") {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
      const hasModified =
        modifiedRowsH.modifiedRowcount > 0 ||
        modifiedRowsL.modifiedRowcount > 0;
      if (hasModified) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장 후 결재상신 하시겠습니까?"
        );
        if (!confirm) return;
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }

      if (!focusedNodeH?.data?.no_po) {
        await showDialog("alter", "결재상신할 발주건을 선택해 주세요.");
        return;
      }

      // 외주발주 헤더는 승인여부를 yn_app 으로 관리(yn_app='Y'→st_po 'R'승인). 미승인건만 상신
      if (focusedNodeH.data.yn_app === "Y") {
        await showDialog("alter", "미승인 상태인 건만 결재상신할 수 있습니다.");
        return;
      }

      await executeApprovalSubmit({
        menuCode: "SN_SCT_POR_REG_001",
        noRef: focusedNodeH.data.no_po,
        userInfo: {
          cd_firm: userInfo.cd_firm,
          cd_biz: userInfo.cd_biz,
          cd_dept: userInfo.cd_dept,
          cd_emp: userInfo.cd_emp,
          cd_user: userInfo.cd_user,
        },
        submitApproval,
        showDialog,
        openWindow: (url) => window.open(url),
        noUrlMessage: "결재창 URL을 수신하지 못했습니다.",
      });
      return;
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const onFocusedRowChanged = async (params: any) => {
    const selectedRow = params;
    if (selectedRow) {
      const no_po = selectedRow.no_po;
      gridRefs.current["grid2"].current.applyFilters({ no_po: no_po });
      gridRefs.current["grid2"].current.selectAndFocusRow(0);
      gridRefs.current["grid3"].current.applyFilters({ no_po: no_po });
      gridRefs.current["grid3"].current.selectAndFocusRow(0);
      setSelectedDocu(no_po);
    } else {
      return;
    }
  };

  const handleContextMenu = async (params: any) => {
    const menuItems = useMenuStore.getState().menuItems;
    switch (params) {
      case "fn_sn_sct_com_sio_reg_02":
        const selectedDataH =
          gridRefs.current["grid1"].current.getSelectedRows();
        const returnData = [selectedDataH[0].no_po, selectedDataH[0].dt_wo];

        const drillDown = menuItems
          ? menuItems.find(
              (item: any) => item?.cd_menu === "SN_SCT_SIO_REG_002"
            )
          : undefined;
        if (drillDown) {
          const filePath =
            `./${drillDown.cd_module}/${drillDown.cd_menu}/${drillDown.cd_menu}`.toUpperCase();

          (window as any).onActivate(
            drillDown.cd_menu,
            drillDown.nm_menu,
            drillDown.cd_module,
            filePath,
            drillDown,
            drillDown.nm_path,
            returnData,
            "fg_drilldown.Registration"
          );
        }
        break;

      case "fn_sn_sct_com_sio_reg_04":
        const selectedData =
          gridRefs.current["grid1"].current.getSelectedRows();
        const returnData1 = [selectedData[0].no_po, selectedData[0].dt_wo];

        const drillDown1 = menuItems
          ? menuItems.find(
              (item: any) => item?.cd_menu === "SN_SCT_SIO_REG_004"
            )
          : undefined;
        if (drillDown1) {
          const filePath1 =
            `./${drillDown1.cd_module}/${drillDown1.cd_menu}/${drillDown1.cd_menu}`.toUpperCase();

          (window as any).onActivate(
            drillDown1.cd_menu,
            drillDown1.nm_menu,
            drillDown1.cd_module,
            filePath1,
            drillDown1,
            drillDown1.nm_path,
            returnData1,
            "fg_drilldown.Registration"
          );
        }
        break;

      case "wf_sn_sct_pod_info":
        const selectedDatah =
          gridRefs.current["grid1"].current.getSelectedRows();
        const returnData2 = [selectedDatah[0].no_po, selectedDatah[0].dt_wo];

        const drillDown2 = menuItems
          ? menuItems.find(
              (item: any) => item?.cd_menu === "SN_SCT_BAS_REG_002"
            )
          : undefined;
        if (drillDown2) {
          const filePath2 =
            `./${drillDown2.cd_module}/${drillDown2.cd_menu}/${drillDown2.cd_menu}`.toUpperCase();

          (window as any).onActivate(
            drillDown2.cd_menu,
            drillDown2.nm_menu,
            drillDown2.cd_module,
            filePath2,
            drillDown2,
            drillDown2.nm_path,
            returnData2,
            "fg_drilldown.Search"
          );
        }
        break;
    }
  };

  const onCellValueChanged1 = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "cd_sct") {
      const sqlQuery2 = {
        sql: "select fg_trns, yn_app from mas_sctype where cd_firm = :cd_firm and cd_sct = :cd_sct;",
        cd_firm: userInfo.cd_firm,
        cd_sct: rowData.cd_sct,
      };
      const params = await getTablebySql(sqlQuery2);
      if (params != null && params.length > 0) {
        rowNode.setDataValue("fg_trns", params[0].fg_trns);
        rowNode.setDataValue("yn_app", params[0].yn_app);
      }
    }
    if (colId === "fg_trns") {
      let fg_vat = "21";
      if (rowData.fg_trns === "1") {
        fg_vat = "21";
      } else if (rowData.fg_trns === "2" || rowData.fg_trns === "3") {
        fg_vat = "23";
      } else if (rowData.fg_trns === "4" || rowData.fg_trns === "5") {
        fg_vat = "56";
      }
      rowNode.setDataValue("fg_vat", fg_vat);
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        node.setDataValue("cd_sct", rowData?.cd_sct);
        // node.setDataValue("st_po", rowData?.st_po);
        node.setDataValue("fg_vat", fg_vat);
      });
    }
    if (colId === "yn_app") {
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        if (rowData.yn_app === "Y") {
          node.setDataValue("st_po", "R");
        } else {
          node.setDataValue("st_po", "F");
        }
      });
    }
    if (colId === "cd_cust") {
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        node.setDataValue("cd_cust_sct", rowData?.cd_cust);
      });
    }
    if (colId === "cd_exch") {
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        node.setDataValue("cd_exch", rowData?.cd_exch);
      });
    }
    if (colId === "fg_sct") {
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        node.setDataValue("fg_sct", rowData?.fg_sct);
      });
      gridRefs.current["grid3"].current.forEachFilteredNode((node: RowNode) => {
        node.setDataValue("fg_sct", rowData?.fg_sct);
      });
    }
    CalAmBulk(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const onCellValueChanged2 = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();

    if (colId === "qt") {
      gridRefs.current["grid3"].current.forEachFilteredNode((node: RowNode) => {
        if (node.data.no_line_pol === rowData.no_line) {
          node.setDataValue("qt", rowData?.qt * node.data.qt_need);
        }
      });
    }
    if (colId === "cd_pjt") {
      const rowCount = gridRefs.current["grid2"].current.getDisplayedRowCount();
      if (rowCount === 1) return;
      if (!rowData.cd_pjt) return;

      let apply = false;
      gridRefs.current["grid2"].current?.forEachFilteredNode(
        (node: RowNode) => {
          if (
            node.data[colId] === "" ||
            node.data[colId] === null ||
            node.data[colId] === undefined
          )
            apply = true;
        }
      );
      if (!apply) return;

      const confirm = await showDialog(
        "confirm",
        "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
      );

      if (confirm) {
        gridRefs.current["grid2"].current?.forEachFilteredNode(
          (node: RowNode) => {
            if (
              node.data.cd_pjt === "" ||
              node.data.cd_pjt === null ||
              node.data.cd_pjt === undefined
            ) {
              node.setDataValue("cd_pjt", rowData.cd_pjt);
              node.setDataValue("nm_pjt", rowData.nm_pjt);
            }
          }
        );
      }
    }

    CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const onCellValueChanged3 = (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const focusedNodeL = gridRefs.current["grid2"].current.focusedRowNode();

    const qtJust = parseFloat(rowData.qt_just) || 0; // 정미수량
    const rtLoss = parseFloat(rowData.rt_loss) || 0; // 로스율
    const qtNeed = parseFloat(rowData.qt_need) || 0; // 실소요량
    const qtPo = focusedNodeL.data.qt; // 발주량

    if (colId === "qt_just" || colId === "rt_loss") {
      rowNode.setDataValue("qt_need", qtJust * (1 + rtLoss / 100));
    }
    if (colId === "qt_need") {
      rowNode.setDataValue("qt", qtPo * qtNeed);
    }
    gridRefs.current["grid3"].current.applyTransaction({ update: [rowData] });
  };

  const onTabChanged = (index: string) => {
    setSelectedTab(index);
  };

  const onRowDataReady = () => {
    if (focusedIndex) {
      gridRefs.current["grid1"].current.selectAndFocusRow(focusedIndex);
    }
  };

  const onAfterPopupClose = async (event: any) => {
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const applyNodes = event.nodes;
    const colId = event.colId;
    if (colId === "cd_item") {
      const itemList = applyNodes
        .map((node: any) => node.data.cd_item)
        .filter((cdItem: string) => cdItem);

      if (itemList.length === 0) return;

      const result = await callApi("SN_SCT_POR_REG_001_SAVE_01_getUm", {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        cd_cust: focusedNodeH.data.cd_cust ?? "",
        cd_exch: focusedNodeH.data.cd_exch ?? "",
        itemList,
      });
      const data = result.data;
      console.log(data);
      applyNodes.forEach((node: any) => {
        const cdItem = node.data.cd_item;
        const um = data?.[cdItem];

        if (um !== undefined) {
          node.setDataValue("um", um, "noEvent");
        } else {
          node.setDataValue("um", 0, "noEvent");
        }
      });
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChanged,
          onCellValueChanged: onCellValueChanged1,
          onContextMenu: handleContextMenu,
          onRowDataReady: onRowDataReady,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
          onBeforeAddRow: onBeforeAddRow2,
          onCellValueChanged: onCellValueChanged2,
          onAfterPopupClose: onAfterPopupClose,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
          onBeforeAddRow: onBeforeAddRow3,
          onCellValueChanged: onCellValueChanged3,
          onContextMenu: handleContextMenu,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "발주정보" }}
            events={grids.grid1.events}
            header={
              <div className="flex justify-end gap-1">
                {isApprovalVisible && (
                  <SnButton
                    text={"결재상신"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Approve");
                    }}
                  />
                )}
                <SnButton
                  text={"요청적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btnApply");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnTab
            titles={["발주내역", "소요량조정"]}
            width={"100%"}
            onTabChanged={onTabChanged}
            header={[
              <SnTabHeader
                onBtnAddClick={onGrid2BtnsClick}
                onBtnDeleteClick={onGrid2BtnsClick}
              />,
              <SnTabHeader
                onBtnAddClick={onGrid2BtnsClick}
                onBtnDeleteClick={onGrid2BtnsClick}
              />,
            ]}
            body={[
              <SnGrid
                ref={gridRefs.current["grid2"]}
                columnConfig={grids.grid2.columns}
                rowData={grids.grid2.dataSource}
                etcProps={{ rowSelection: "multiple" }}
                events={grids.grid2.events}
              />,
              <SnGrid
                ref={gridRefs.current["grid3"]}
                columnConfig={grids.grid3.columns}
                rowData={grids.grid3.dataSource}
                etcProps={{ rowSelection: "multiple" }}
                events={grids.grid3.events}
              />,
            ]}
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
});

export default SN_SCT_POR_REG_001;
